import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  legalPage: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '24px',
    boxSizing: 'border-box',
  },
  legalContent: {
    maxWidth: '920px',
    width: '100%',
    lineHeight: '1.5',
    color: tokens.colorNeutralForeground1,
  },
  legalList: {
    marginTop: '8px',
    marginBottom: '16px',
  },
});

export const TermsOfServiceContent: React.FC = () => {
  const styles = useStyles();

  return (
    <main className={styles.legalPage}>
      <article className={styles.legalContent}>
        <h1>Terms of Service</h1>
        <p>Last updated: February 28, 2026</p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using our application (“the Service”), you agree to be bound by these
          Terms of Service (“Terms”). If you do not agree, you may not use the Service.
        </p>
        <p>
          The Service provides a scheduling and session management platform that allows customers to
          book appointments with Guides based on availability.
        </p>

        <h2>2. Description of Service</h2>
        <p>The Service enables:</p>
        <ul className={styles.legalList}>
          <li>Customers to book appointments with Guides</li>
          <li>Guides to manage their availability</li>
          <li>Automatic blocking of time in a Guide’s Google Calendar</li>
          <li>Tracking completion of scheduled sessions</li>
        </ul>
        <p>
          The Service acts only as a scheduling coordination tool and does not conduct or record the
          sessions themselves.
        </p>

        <h2>3. User Accounts</h2>
        <h3>Guides</h3>
        <p>
          Guides must sign in using their Google account and grant calendar access to enable
          scheduling functionality.
        </p>
        <p>Guides are responsible for:</p>
        <ul className={styles.legalList}>
          <li>maintaining access to their Google account</li>
          <li>ensuring availability schedules are accurate</li>
          <li>managing their booked sessions</li>
        </ul>

        <h3>Customers</h3>
        <p>
          Customers may provide contact details for booking purposes. Customers are responsible for
          providing accurate information when scheduling sessions.
        </p>

        <h2>4. Google Calendar Integration</h2>
        <p>The Service integrates with Google Calendar to:</p>
        <ul className={styles.legalList}>
          <li>check availability</li>
          <li>create booking time blocks</li>
          <li>update scheduled sessions</li>
          <li>cancel scheduled sessions</li>
        </ul>
        <p>
          The Service only manages events created by the platform and does not modify unrelated
          calendar entries.
        </p>
        <p>
          Users may revoke calendar access at any time from their Google Account permissions page.
          Revoking access may prevent future bookings from being scheduled properly.
        </p>

        <h2>5. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul className={styles.legalList}>
          <li>misuse the Service</li>
          <li>attempt unauthorized access to other users’ data</li>
          <li>interfere with system operations</li>
          <li>use the platform for unlawful purposes</li>
          <li>attempt to reverse engineer or disrupt the system</li>
        </ul>

        <h2>6. Data and Privacy</h2>
        <p>
          Your use of the Service is also governed by our Privacy Policy. The Privacy Policy
          explains how we collect, use, and protect personal data and Google Calendar data.
        </p>

        <h2>7. Availability of Service</h2>
        <p>
          We aim to provide reliable service but do not guarantee uninterrupted availability. The
          Service may be temporarily unavailable due to maintenance, updates, or technical issues.
        </p>
        <p>We are not responsible for missed sessions caused by:</p>
        <ul className={styles.legalList}>
          <li>internet outages</li>
          <li>revoked Google permissions</li>
          <li>incorrect availability provided by the Guide</li>
          <li>third-party service failures (including Google services)</li>
        </ul>

        <h2>8. Termination</h2>
        <p>We may suspend or terminate access to the Service if a user:</p>
        <ul className={styles.legalList}>
          <li>violates these Terms</li>
          <li>misuses the platform</li>
          <li>creates operational or security risk</li>
        </ul>
        <p>Users may stop using the Service at any time.</p>

        <h2>9. Limitation of Liability</h2>
        <p>The Service is provided “as is” without warranties of any kind.</p>
        <p>We are not liable for:</p>
        <ul className={styles.legalList}>
          <li>missed appointments</li>
          <li>scheduling conflicts</li>
          <li>user disputes</li>
          <li>indirect or consequential damages arising from use of the platform</li>
        </ul>

        <h2>10. Changes to the Service</h2>
        <p>
          We may modify or improve features of the Service at any time. Continued use of the Service
          after updates constitutes acceptance of the revised Terms.
        </p>

        <h2>11. Contact</h2>
        <p>For questions regarding these Terms, contact:</p>
        <p>Email: client@leelalife.com</p>
        <p>
          This Terms of Service is accessible at <a href="/terms">/terms</a>.
        </p>
      </article>
    </main>
  );
};
